import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';
import { distanceKm } from '../distance_km.js';
import { useStableCallback } from '../use_stable_callback.js';
import { liveMap, type MapboxInstance, type MapCoordinates } from './results_map.js';
import './map_search_area.css';

/*
 * O "buscar nesta área" dos mapas de resultados (hotel #2010/#3114, carro
 * #3995): a leitura da área visível, o botão e a máquina de estado dele. O mapa
 * de cada produto só liga o hook e desenha o botão; quem busca é o host.
 */

/** A caixa VISÍVEL do mapa (viewport) em graus — os quatro cantos do que está na
 *  tela no momento do gesto. */
type MapBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

/** O que o gesto "buscar nesta área" entrega ao HOST: a caixa visível, o centro
 *  dela e o raio (km) do círculo que a cobre (centro→canto). É o suficiente para
 *  o host montar a busca do fornecedor — o centro como lugar e o raio como
 *  `restrictRadiusTo` —, aplicando a guarda de raio do servidor antes de mandar.
 *  O kit NÃO faz IO: só mede a área e devolve; quem busca é o host. */
export type MapSearchArea = {
  bounds: MapBounds;
  center: MapCoordinates;
  radiusKm: number;
};

/** Rótulos do controle "buscar nesta área" — copy por prop, como todo texto do
 *  kit (o host traduz). `searchLabel` é o estado ocioso ("Buscar nesta área");
 *  `searchingLabel` é o estado em busca ("Buscando…"); `zoomInLabel` ("Aproxime
 *  o mapa para buscar") toma o lugar do botão quando a área passa do raio que o
 *  fornecedor aceita (`maxRadiusKm` do hook). */
export type MapSearchAreaCopy = {
  searchLabel: string;
  searchingLabel: string;
  zoomInLabel?: string;
};

/** Onde o botão "buscar nesta área" cai quando o chamador não desenha cromo por
 *  cima (sem barra de busca): um respiro do topo. Com cromo, ele desce para a
 *  borda de baixo dele (`overlays.top`), logo abaixo da barra de busca. */
export const MAP_SEARCH_AREA_TOP_FALLBACK = 16;

/** Lê a ÁREA visível do mapa vivo no formato do kit: a caixa (viewport), o centro
 *  e o raio (km) do círculo que cobre a caixa — centro→canto, um SUPERCONJUNTO do
 *  que está na tela. Devolve `undefined` quando o mapa ainda não sabe seus limites
 *  (sem `getBounds`, sem cantos), e aí o clique é ignorado sem quebrar. */
function readMapSearchArea(mapInstance: MapboxMap): MapSearchArea | undefined {
  const mapBounds = mapInstance.getBounds();
  const northEast = mapBounds?.getNorthEast();
  const southWest = mapBounds?.getSouthWest();
  const center = mapBounds?.getCenter();
  if (!northEast || !southWest || !center) return undefined;
  const centerCoord = { latitude: center.lat, longitude: center.lng };
  const bounds = {
    north: northEast.lat,
    south: southWest.lat,
    east: northEast.lng,
    west: southWest.lng,
  };
  // O raio COBRE a caixa: o canto mais LONGE do centro. E ele não é sempre o NE —
  // longe do equador a projeção estica um lado, então o SW pode ficar mais longe
  // que o NE. Medir os QUATRO cantos e pegar o maior é o que garante que o círculo
  // não deixa de fora parte do que está na tela quando o host aplica `restrictRadiusTo`.
  const radiusKm = Math.max(
    distanceKm(centerCoord, { latitude: bounds.north, longitude: bounds.east }),
    distanceKm(centerCoord, { latitude: bounds.north, longitude: bounds.west }),
    distanceKm(centerCoord, { latitude: bounds.south, longitude: bounds.east }),
    distanceKm(centerCoord, { latitude: bounds.south, longitude: bounds.west }),
  );
  return { bounds, center: centerCoord, radiusKm };
}

/**
 * O botão "buscar nesta área" (padrão Airbnb/Booking): centrado na horizontal e
 * logo ABAIXO da barra de busca (o chamador diz onde a barra termina em
 * `overlays.top`). É o único aviso na tela de que "dá pra buscar aqui" — e, no
 * clique, de que "está buscando".
 *
 * Travado durante a busca por `aria-disabled` e não por `disabled`: o átomo
 * desabilitado do DS cai para ~50% de opacidade, e apagar visualmente o ÚNICO
 * sinal de "algo está acontecendo" é o oposto do que ele existe para fazer. O
 * clique repetido é barrado no handler; a opacidade fica cheia. (issue #2010)
 */
function MapSearchAreaButton({
  copy,
  loading,
  topOffset,
  onSearch,
}: {
  readonly copy: MapSearchAreaCopy;
  readonly loading: boolean;
  readonly topOffset: number;
  readonly onSearch: () => void;
}): ReactNode {
  return (
    <div className="iuk-map-search-area" style={{ top: topOffset }}>
      <Button
        type="button"
        variant="outline"
        className="rounded-full shadow-md"
        aria-disabled={loading || undefined}
        aria-busy={loading || undefined}
        data-loading={loading || undefined}
        onClick={() => {
          if (loading) return;
          onSearch();
        }}
      >
        {loading ? <Spinner aria-hidden="true" /> : <MagnifyingGlassIcon aria-hidden="true" />}
        {loading ? copy.searchingLabel : copy.searchLabel}
      </Button>
    </div>
  );
}

/** O botão "buscar nesta área" só entra quando ARMADO por um gesto e com copy —
 *  extraído do corpo do mapa como os controles de vista, para o cromo do mapa
 *  não inchar a função. Com a área larga demais para o fornecedor, entra no
 *  lugar dele o pedido de aproximar: um aviso, não um botão — não há o que
 *  clicar até a pessoa dar zoom. */
export function renderMapSearchArea({
  searchArea,
  copy,
  topOffset,
}: {
  readonly searchArea: ReturnType<typeof useMapSearchArea>;
  readonly copy?: MapSearchAreaCopy;
  readonly topOffset: number;
}): ReactNode {
  if (!searchArea.armed || !copy) return null;
  if (searchArea.tooWide && copy.zoomInLabel && !searchArea.loading) {
    return (
      <div className="iuk-map-search-area" style={{ top: topOffset }}>
        <p
          role="status"
          className="rounded-full border bg-background px-4 py-2 text-sm font-medium text-muted-foreground shadow-md"
        >
          {copy.zoomInLabel}
        </p>
      </div>
    );
  }
  return (
    <MapSearchAreaButton
      copy={copy}
      loading={searchArea.loading}
      topOffset={topOffset}
      onSearch={searchArea.runSearch}
    />
  );
}

/**
 * A máquina de estado do "buscar nesta área", inteira aqui e fora do JSX porque é
 * concorrência, não layout. Duas peças de estado — `armed` (o botão está na tela?)
 * e `loading` (a busca dele está no ar?) — e um SELO de movimento (`moveSeqRef`)
 * que cada gesto humano incrementa.
 *
 * As três regras que o selo resolve (issue #2010):
 *  - Só GESTO arma: o `moveend` só conta quando traz `originalEvent` (mouse/touch);
 *    fit inicial, resize e recentralizar chegam sem ele e são silêncio. Os botões
 *    `+`/`−` marcam à mão (`markGesture`), porque a chamada programática deles não
 *    traz `originalEvent`.
 *  - A busca guarda o selo no início e só APAGA o botão se ele não mudou até ela
 *    terminar: mover o mapa durante a busca faz a resposta (da área velha) chegar
 *    sem apagar a oferta da área NOVA.
 *  - Coleta inicial (`pending`) não arma: sem base na tela não há área a reofertar.
 *
 * `maxRadiusKm` é o teto do fornecedor (o carro só procura agências a até 30 km,
 * #3995): todo `moveend` — gesto ou não, inclusive o fim do zoom dos `+`/`−` —
 * remede a área, e acima do teto `tooWide` troca o botão pelo pedido de aproximar.
 */
export function useMapSearchArea({
  canRenderMap,
  mapRef,
  instance,
  onSearchArea,
  pending,
  maxRadiusKm,
}: {
  readonly canRenderMap: boolean;
  readonly mapRef: { readonly current: MapboxMap | null };
  readonly instance: MapboxInstance | null;
  /** Ausente ⇒ o recurso está desligado (sem host que busque, ou mapa só de leitura). */
  readonly onSearchArea?: (area: MapSearchArea) => void | Promise<void>;
  readonly pending: boolean;
  readonly maxRadiusKm?: number;
}): {
  readonly armed: boolean;
  readonly loading: boolean;
  readonly tooWide: boolean;
  readonly runSearch: () => void;
  readonly markGesture: () => void;
} {
  const [armed, setArmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tooWide, setTooWide] = useState(false);
  const moveSeqRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const enabled = Boolean(onSearchArea);

  const markGesture = useStableCallback(() => {
    if (!enabled || pending) return;
    moveSeqRef.current += 1;
    setArmed(true);
  });

  // O recurso desligou, ou a coleta inicial recomeçou: uma área armada de antes
  // não pode sobreviver a um estado sem base re-ofertável. Reset during render,
  // on the change itself.
  const activity = JSON.stringify([enabled, pending]);
  const [seenActivity, setSeenActivity] = useState(activity);
  if (seenActivity !== activity) {
    setSeenActivity(activity);
    if (!enabled || pending) {
      setArmed(false);
      setLoading(false);
    }
  }

  useEffect(() => {
    const mapInstance = liveMap(mapRef, instance);
    if (!enabled || !canRenderMap || !mapInstance) return undefined;
    const onMoveEnd = (event: { originalEvent?: unknown }) => {
      if (maxRadiusKm !== undefined) {
        const area = readMapSearchArea(mapInstance);
        if (area) setTooWide(area.radiusKm > maxRadiusKm);
      }
      // Só GESTO: `originalEvent` presente = mouse/touch humano; ausente =
      // reenquadramento programático (flyTo/easeTo/fitBounds/jumpTo internos).
      if (event.originalEvent == null) return;
      markGesture();
    };
    mapInstance.on('moveend', onMoveEnd);
    return () => {
      mapInstance.off('moveend', onMoveEnd);
    };
    // `markGesture` é estável (`useStableCallback`): nas deps, não re-arma o ouvinte.
  }, [canRenderMap, enabled, instance, mapRef, markGesture, maxRadiusKm]);

  const runSearch = useStableCallback(() => {
    const mapInstance = mapRef.current;
    if (!enabled || loading || !mapInstance) return;
    const area = readMapSearchArea(mapInstance);
    if (!area) return;
    const dispatchedSeq = moveSeqRef.current;
    setLoading(true);
    const settle = () => {
      if (!mountedRef.current) return;
      setLoading(false);
      // Só apaga se NENHUM gesto novo entrou enquanto a busca estava no ar.
      if (moveSeqRef.current === dispatchedSeq) setArmed(false);
    };
    // `onSearchArea(area)` DENTRO do `.then`, não como argumento do `Promise.resolve`:
    // ela devolve `void | Promise`, então pode lançar SÍNCRONO — e um throw fora da
    // cadeia escaparia sem `settle`, deixando o botão preso em "buscando" para
    // sempre. Aqui o throw síncrono vira rejeição e ainda settla.
    Promise.resolve()
      .then(() => onSearchArea?.(area))
      .then(settle, settle);
  });

  return { armed, loading, tooWide, runSearch, markGesture };
}
